import { useMemo } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type {
  AimApplicationDraft,
  AimFlowDraft,
  AimWorkloadDraft,
} from '../../../../model/aimDrafts';

export type OrphanDetection = {
  workloadOrphanIds: Set<string>;
  flowOrphanIds: Set<string>;
  totalOrphans: number;
  applicationReferencingWorkloads: (appId: string) => string[];
  workloadReferencingFlows: (wlId: string) => string[];
  // TODO: temporary solution — applicationReferencingFlows helper removed
  // because Flow no longer carries applicationId on the Draft. Restore if
  // BE rebase ever puts it back (unlikely).
};

export const useAimOrphanDetection = (
  form: UseFormReturn<FieldValues>,
): OrphanDetection => {
  const applications = (useWatch({
    control: form.control,
    name: 'aimApplications',
  }) ?? []) as AimApplicationDraft[];
  const workloads = (useWatch({
    control: form.control,
    name: 'aimWorkloads',
  }) ?? []) as AimWorkloadDraft[];
  const flows = (useWatch({ control: form.control, name: 'aimFlows' }) ??
    []) as AimFlowDraft[];

  return useMemo(() => {
    const appIds = new Set(applications.map((a) => a.id));
    const wlIds = new Set(workloads.map((w) => w.id));

    // Required-FK rule: empty OR unresolved → orphan. Permanent correctness fix:
    // the previous Boolean(id) check let CSV-imported empty refs slip past the
    // Save guard. workload.applicationId, flow.srcWorkloadId, flow.dstWorkloadId
    // are all REQUIRED at BE level.
    const workloadOrphanIds = new Set(
      workloads
        .filter((w) => !w.applicationId || !appIds.has(w.applicationId))
        .map((w) => w.id),
    );
    const flowOrphanIds = new Set(
      flows
        .filter(
          (f) =>
            !f.srcWorkloadId ||
            !wlIds.has(f.srcWorkloadId) ||
            !f.dstWorkloadId ||
            !wlIds.has(f.dstWorkloadId),
        )
        .map((f) => f.id),
    );

    return {
      workloadOrphanIds,
      flowOrphanIds,
      totalOrphans: workloadOrphanIds.size + flowOrphanIds.size,
      applicationReferencingWorkloads: (appId) =>
        workloads.filter((w) => w.applicationId === appId).map((w) => w.id),
      workloadReferencingFlows: (wlId) =>
        flows
          .filter((f) => f.srcWorkloadId === wlId || f.dstWorkloadId === wlId)
          .map((f) => f.id),
    };
  }, [applications, workloads, flows]);
};
